import React, { useEffect, useState } from "react";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { ShowToastMessage } from "../../helper/ShowToastMessage";

// Syllabus save/load templates. Self-contained: the template bar plus the
// Save, Load, and confirmation dialogs. Backend is organizer-scoped (admins
// act on the event organizer's templates), so this just passes event_id.
//
// Props:
//   eventId   - the event whose syllabus is being templated
//   isEmpty   - true if the current syllabus has no competitions (save warns)
//   onLoaded  - called after a successful load to refresh the syllabus view

const GREEN = "#68AD3F";
const DANGER = "#C62828";
const INFO = "#2563EB";

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1050,
  padding: "16px",
};
const card = {
  background: "#fff",
  borderRadius: "10px",
  padding: "24px",
  width: "520px",
  maxWidth: "100%",
  maxHeight: "88vh",
  overflowY: "auto",
  boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
};
const btnBase = {
  padding: "9px 18px",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: 500,
  fontSize: "14px",
  minHeight: "40px",
};
const btnGreen = { ...btnBase, border: "none", background: GREEN, color: "#fff" };
const btnOutline = { ...btnBase, border: "1px solid #c8c8c8", background: "#fff", color: "#323232" };
const btnDanger = { ...btnBase, border: "none", background: DANGER, color: "#fff" };
const linkBtn = (color) => ({
  border: "none",
  background: "transparent",
  color,
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: 500,
  padding: "6px 8px",
});

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6, verticalAlign: "-2px" }}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6, verticalAlign: "-2px" }}>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
  </svg>
);

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (e) {
    return "";
  }
};

const SyllabusTemplates = ({ eventId, isEmpty, onLoaded }) => {
  const base = `${process.env.REACT_APP_BASE_URL}/syllabus-template`;

  const [showLoad, setShowLoad] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const [name, setName] = useState("");
  const [nameErr, setNameErr] = useState("");
  const [existsId, setExistsId] = useState(""); // set when name collides -> overwrite prompt
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  const [confirm, setConfirm] = useState(null); // { kind, template }
  const [busy, setBusy] = useState(false);

  // ---- Esc closes the top-most layer ----
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (confirm) setConfirm(null);
      else if (showSave) closeSave();
      else if (showLoad) setShowLoad(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const fetchTemplates = async () => {
    try {
      setListLoading(true);
      const res = await serviceConsumer("GET", `${base}?event_id=${eventId}`);
      setTemplates(res?.templates || []);
    } catch (e) {
      ShowToastMessage("error", e.messages || "Could not load templates");
    } finally {
      setListLoading(false);
    }
  };

  const openLoad = () => {
    setSelectedId("");
    setShowLoad(true);
    fetchTemplates();
  };

  const openSave = () => {
    setName("");
    setNameErr("");
    setExistsId("");
    setConfirmEmpty(false);
    setShowSave(true);
  };
  const closeSave = () => {
    if (busy) return;
    setShowSave(false);
  };

  // ---- Save ----
  const doSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameErr("Please enter a template name.");
      return;
    }
    if (isEmpty && !confirmEmpty) {
      setNameErr("");
      setConfirmEmpty(true);
      return;
    }
    try {
      setBusy(true);
      const res = await serviceConsumer("POST", base, { event_id: eventId, name: trimmed });
      ShowToastMessage("success", `Template "${res?.template?.name || trimmed}" saved`);
      setShowSave(false);
    } catch (error) {
      // serviceConsumer surfaces the raw response body on error.error.response.data.
      // Backend returns 409 + exists:true + templateId when the name is taken.
      const data = error?.error?.response?.data || {};
      if (data.exists && data.templateId) {
        setExistsId(data.templateId);
        setNameErr("");
      } else {
        setNameErr(error.messages || data.message || "Could not save template");
      }
    } finally {
      setBusy(false);
    }
  };

  const doOverwriteFromSave = async () => {
    try {
      setBusy(true);
      await serviceConsumer("PUT", `${base}/${existsId}`, { event_id: eventId });
      ShowToastMessage("success", `Template "${name.trim()}" updated`);
      setShowSave(false);
    } catch (error) {
      setNameErr(error.messages || "Could not overwrite template");
    } finally {
      setBusy(false);
    }
  };

  // ---- Confirm-driven actions (replace / overwrite / delete) ----
  const runConfirm = async () => {
    if (!confirm) return;
    const { kind, template } = confirm;
    try {
      setBusy(true);
      if (kind === "replace") {
        await serviceConsumer("POST", `${base}/${template._id}/load`, { event_id: eventId });
        ShowToastMessage("success", `Syllabus loaded from "${template.name}"`);
        setConfirm(null);
        setShowLoad(false);
        if (onLoaded) onLoaded();
      } else if (kind === "overwrite") {
        await serviceConsumer("PUT", `${base}/${template._id}`, { event_id: eventId });
        ShowToastMessage("success", `Template "${template.name}" updated`);
        setConfirm(null);
        fetchTemplates();
      } else if (kind === "delete") {
        await serviceConsumer("DELETE", `${base}/${template._id}`);
        ShowToastMessage("success", `Template "${template.name}" deleted`);
        if (selectedId === template._id) setSelectedId("");
        setConfirm(null);
        fetchTemplates();
      }
    } catch (error) {
      // e.g. 409 when the event already has registrations (load blocked).
      ShowToastMessage("error", error.messages || error?.data?.message || "Action failed");
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmCopy = () => {
    if (!confirm) return {};
    const n = confirm.template?.name;
    if (confirm.kind === "replace")
      return {
        title: "Replace current syllabus?",
        msg: `Loading "${n}" will permanently replace all levels and competitions in this event's syllabus. This cannot be undone.`,
        action: "Replace syllabus",
      };
    if (confirm.kind === "overwrite")
      return {
        title: "Overwrite template?",
        msg: `This will replace "${n}" with the current event's syllabus. This cannot be undone.`,
        action: "Overwrite",
      };
    return {
      title: "Delete template?",
      msg: `Are you sure you want to delete "${n}"? This cannot be undone.`,
      action: "Delete",
    };
  };

  return (
    <>
      {/* Template bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          background: "#f4f4f4",
          border: "1px solid #e6e6e6",
          borderRadius: "8px",
          padding: "12px 16px",
          margin: "12px 0 20px",
        }}
      >
        <span style={{ fontWeight: 500, color: "#424242" }}>Syllabus templates</span>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button type="button" style={btnOutline} onClick={openLoad}>
            <DownloadIcon />Load template
          </button>
          <button type="button" style={btnGreen} onClick={openSave}>
            <SaveIcon />Save as template
          </button>
        </div>
      </div>

      {/* Save modal */}
      {showSave && (
        <div style={overlay} onClick={closeSave}>
          <div style={{ ...card, width: "440px" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "16px" }}>
              Save syllabus as template
            </div>
            <label style={{ fontSize: "13px", fontWeight: 500, color: "#424242" }}>
              Template name
            </label>
            <input
              type="text"
              value={name}
              autoFocus
              maxLength={255}
              placeholder="e.g. Standard Ontario Feis"
              onChange={(e) => {
                setName(e.target.value);
                setNameErr("");
                setExistsId("");
                setConfirmEmpty(false);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                marginTop: "6px",
                border: "1px solid #c8c8c8",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            {nameErr && (
              <div role="alert" style={{ color: DANGER, fontSize: "13px", marginTop: "8px" }}>{nameErr}</div>
            )}

            {confirmEmpty && !existsId && (
              <div style={{ background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "6px", padding: "10px 12px", fontSize: "13px", color: "#6d4c00", marginTop: "12px" }}>
                The current syllabus is empty. Save it as a template anyway?
              </div>
            )}

            {existsId && (
              <div style={{ background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: "6px", padding: "10px 12px", fontSize: "13px", color: "#6d4c00", marginTop: "12px" }}>
                A template named "{name.trim()}" already exists. Overwrite it?
                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                  <button type="button" style={{ ...btnDanger, minHeight: "34px", padding: "6px 14px" }} disabled={busy} onClick={doOverwriteFromSave}>
                    Yes, overwrite
                  </button>
                  <button type="button" style={{ ...btnOutline, minHeight: "34px", padding: "6px 14px" }} disabled={busy} onClick={() => setExistsId("")}>
                    No
                  </button>
                </div>
              </div>
            )}

            {!existsId && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" style={btnOutline} disabled={busy} onClick={closeSave}>Cancel</button>
                <button type="button" style={btnGreen} disabled={busy} onClick={doSave}>
                  {confirmEmpty ? "Save anyway" : "Save"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Load modal */}
      {showLoad && (
        <div style={overlay} onClick={() => !busy && setShowLoad(false)}>
          <div style={card} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: "18px", fontWeight: 600 }}>Load syllabus template</div>
            <div style={{ fontSize: "13px", color: "#666", margin: "4px 0 16px" }}>
              Select a saved template to load into this event.
            </div>

            {listLoading && <div style={{ padding: "24px 0", textAlign: "center", color: "#666" }}>Loading templates…</div>}

            {!listLoading && templates.length === 0 && (
              <div style={{ padding: "24px 12px", textAlign: "center", color: "#666", fontSize: "14px" }}>
                No saved templates yet. Save your current syllabus to create your first template.
              </div>
            )}

            {!listLoading && templates.map((t) => {
              const selected = selectedId === t._id;
              return (
                <div
                  key={t._id}
                  onClick={() => setSelectedId(t._id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    border: `1.5px solid ${selected ? GREEN : "#e0e0e0"}`,
                    borderRadius: "8px",
                    padding: "12px 14px",
                    marginBottom: "10px",
                    cursor: "pointer",
                    background: selected ? "#F4FAEF" : "#fff",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: "#222" }}>{t.name}</div>
                    <div style={{ fontSize: "12px", color: "#777", marginTop: "2px" }}>
                      {t.level_count} levels · {t.competition_count} competitions · Saved {fmtDate(t.updatedAt)}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                    <button type="button" style={linkBtn(INFO)} onClick={(e) => { e.stopPropagation(); setConfirm({ kind: "overwrite", template: t }); }}>
                      Overwrite
                    </button>
                    <button type="button" style={linkBtn(DANGER)} onClick={(e) => { e.stopPropagation(); setConfirm({ kind: "delete", template: t }); }}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px" }}>
              <button type="button" style={btnOutline} disabled={busy} onClick={() => setShowLoad(false)}>Cancel</button>
              <button
                type="button"
                style={{ ...btnGreen, opacity: selectedId ? 1 : 0.5, cursor: selectedId ? "pointer" : "not-allowed" }}
                disabled={!selectedId || busy}
                onClick={() => {
                  const t = templates.find((x) => x._id === selectedId);
                  if (t) setConfirm({ kind: "replace", template: t });
                }}
              >
                Load template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog (replace / overwrite / delete) */}
      {confirm && (
        <div style={{ ...overlay, zIndex: 1060 }} onClick={() => !busy && setConfirm(null)}>
          <div style={{ ...card, width: "420px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#FDECEA", color: DANGER, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: "26px", fontWeight: 700 }} aria-hidden="true">!</div>
            <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "8px" }}>{confirmCopy().title}</div>
            <div style={{ fontSize: "14px", color: "#555", lineHeight: 1.5 }}>{confirmCopy().msg}</div>
            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
              <button type="button" style={btnOutline} disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" style={btnDanger} disabled={busy} onClick={runConfirm}>{confirmCopy().action}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SyllabusTemplates;
